import { ExternTierarztAnlegenDialogComponent } from '../extern-tierarzt-anlegen-dialog/extern-tierarzt-anlegen-dialog.component';
import { KieneAutocompleteComponent } from '../../../kiene-autocomplete/kiene-autocomplete.component';
import { StammdatenService } from '../../stammdaten.service';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { MessageService } from '../../../../services/message-service/message.service';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ExternTierarzt } from '../extern-tierarzt';
import { KieneApi } from '../../../../services/backend-api/kiene-backend-api.service';

@Component({
    selector: 'kiene-extern-tierarzt-details',
    templateUrl: './extern-tierarzt-details.component.html',
    styleUrls: ['./extern-tierarzt-details.component.scss'],
})
export class ExternTierarztDetailsComponent implements OnInit, OnDestroy {
    meineSubscription: Subscription;
    tierarztPath = `${this.apiBaseUrl}extern_tierarzt/read_all.php`;
    meineTieraerzte: ExternTierarzt[] = [];
    meineFilter = '';
    isLoading = false;
    isLoadingMeine = false;
    searchVisible = true;

    private readonly meineFilterThreshold = 5;

    @ViewChild('nameAutocomplete') nameAutocomplete: KieneAutocompleteComponent;

    @Input('externTierarzt') externTierarzt: ExternTierarzt;
    @Input() allowChange = false;
    @Input() editMode = false;

    @Output('saved') saved: EventEmitter<ExternTierarzt> = new EventEmitter<ExternTierarzt>();

    constructor(
        private stammdatenService: StammdatenService,
        private messageService: MessageService,
        private dialog: MatDialog,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnDestroy(): void {
        this.meineSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.loadMeineTieraerzte();
        if (!this.externTierarzt) {
            this.externTierarzt = new ExternTierarzt();
        }
        if (this.hasSelection() && !this.allowChange) {
            this.searchVisible = false;
        }
    }

    hasSelection(): boolean {
        return Number(this.externTierarzt?.extern_tierarzt_id || 0) > 0;
    }

    showSearch(): boolean {
        if (!this.searchVisible) {
            return false;
        }
        return !this.hasSelection() || this.allowChange;
    }

    showPickerSections(): boolean {
        return !this.hasSelection() || this.allowChange;
    }

    showChangeButton(): boolean {
        return this.hasSelection() && (this.allowChange || !this.searchVisible);
    }

    useSplitLayout(): boolean {
        return this.editMode && this.hasSelection() && this.showPickerSections();
    }

    autocompletePanelWidth(): string {
        return 'min(680px, calc(100vw - 48px))';
    }

    displayValue(value?: string): string {
        const trimmed = (value || '').trim();
        return trimmed.length > 0 ? trimmed : '–';
    }

    displayPlzOrt(): string {
        const plzOrt = [this.externTierarzt?.plz, this.externTierarzt?.ort].filter(v => (v || '').trim().length > 0).join(' ');
        return this.displayValue(plzOrt || undefined);
    }

    showMeineSection(): boolean {
        return this.showPickerSections() && (this.isLoadingMeine || this.meineTieraerzte.length > 0);
    }

    showMeineFilter(): boolean {
        return !this.isLoadingMeine && this.meineTieraerzte.length >= this.meineFilterThreshold;
    }

    filteredMeineTieraerzte(): ExternTierarzt[] {
        const q = this.meineFilter.trim().toLowerCase();
        if (!q) {
            return this.meineTieraerzte;
        }
        return this.meineTieraerzte.filter(t => this.meineTierarztSearchText(t).includes(q));
    }

    displayMeineSubtitle(tierarzt: ExternTierarzt): string {
        const plzOrt = [tierarzt?.plz, tierarzt?.ort].filter(v => (v || '').trim().length > 0).join(' ');
        if (plzOrt) {
            return plzOrt;
        }
        const telefon = (tierarzt?.telefon || '').trim();
        if (telefon) {
            return telefon;
        }
        const email = (tierarzt?.email || '').trim();
        return email;
    }

    trackByExternTierarztId(_index: number, tierarzt: ExternTierarzt): number {
        return Number(tierarzt?.extern_tierarzt_id || 0);
    }

    private meineTierarztSearchText(tierarzt: ExternTierarzt): string {
        return [tierarzt?.name, tierarzt?.ort, tierarzt?.plz, tierarzt?.telefon, tierarzt?.email]
            .filter(v => (v || '').trim().length > 0)
            .join(' ')
            .toLowerCase();
    }

    clearSelection(): void {
        this.externTierarzt = new ExternTierarzt();
        this.searchVisible = true;
        this.meineFilter = '';
        this.nameAutocomplete?.clear();
    }

    confirmSelection(): void {
        if (!this.hasSelection()) {
            this.messageService.infoMessage('Bitte einen Tierarzt auswählen oder anlegen.');
            return;
        }
        this.saved.emit(this.externTierarzt);
    }

    selectedTierarzt(tierarzt: ExternTierarzt): void {
        if (!tierarzt) {
            return;
        }
        this.applySelection(tierarzt);
    }

    selectFromMeine(tierarzt: ExternTierarzt): void {
        const id = Number(tierarzt?.extern_tierarzt_id || 0);
        if (id > 0) {
            this.stammdatenService.getExternTierarzt(id).subscribe({
                next: full => this.applySelection(full),
                error: () => this.applySelection({ ...tierarzt }),
            });
            return;
        }
        this.applySelection({ ...tierarzt });
    }

    private applySelection(tierarzt: ExternTierarzt): void {
        this.externTierarzt = { ...tierarzt };
        if (!this.allowChange) {
            this.searchVisible = false;
        }
    }

    createNewExternTierarzt(tierarztName?: string): void {
        const et = new ExternTierarzt();
        et.name = tierarztName;
        this.dialog
            .open(ExternTierarztAnlegenDialogComponent, { data: et })
            .afterClosed()
            .subscribe({
                next: (tierarzt: ExternTierarzt) => {
                    if (tierarzt) {
                        this.selectedTierarzt(tierarzt);
                        this.loadMeineTieraerzte();
                    }
                },
            });
    }

    private loadMeineTieraerzte(): void {
        this.meineSubscription?.unsubscribe();
        this.isLoadingMeine = true;
        this.meineSubscription = this.stammdatenService.ladeMeineExternTieraerzte().subscribe({
            next: (response: KieneApi) => {
                this.meineTieraerzte = response.records || [];
                this.isLoadingMeine = false;
            },
            error: err => {
                this.isLoadingMeine = false;
                this.messageService.errorMessage('Fehler beim Laden Ihrer Tierärzte', err);
            },
        });
    }
}
